// Shared PDF helpers. Everything here must run in BOTH the browser (admin
// dashboard) and Node (auto-sync worker), so nothing may touch Image, canvas,
// document, window, localStorage or fetch.
//
// The originals lived in frontend/src/utils/formPdfHelpers.js and, duplicated,
// at the top of several admin pages.

import { LOGO } from "./assets.js";

export const PRIMARY_FORM = [139, 22, 52];

export function drawRoundedRect(doc, x, y, width, height, radius, fillColor, strokeColor) {
  doc.setFillColor(...fillColor);
  doc.setDrawColor(...strokeColor);
  doc.setLineWidth(0.5);
  doc.roundedRect(x, y, width, height, radius, radius, "FD");
}

/**
 * Draws the Taylor logo into the coloured header band.
 *
 * Replaces the `new Image()` + canvas-upscale dance the pages used to do. The
 * logo is a fixed asset, so its dimensions are known at build time (see
 * src/assets.js) and no image decoding is needed at all - which is what lets
 * this run under Node.
 */
export function drawHeaderLogo(doc, options = {}) {
  const { x = 10, headerHeight = 30, width = 70 } = options;
  // Some pages placed the logo in a fixed box rather than centring it in the
  // header band - honour those numbers exactly so the extracted layout renders
  // the document it always did.
  const height = options.height != null ? options.height : width * LOGO.aspectRatio;
  const y = options.y != null ? options.y : (headerHeight - height) / 2;
  try {
    doc.addImage(LOGO.dataUri, "PNG", x, y, width, height, undefined, "NONE");
  } catch {
    /* a missing logo must never break the document */
  }
  return { width, height, y };
}

/**
 * The coloured banner several forms open with: logo on the left, wrapped title
 * to its right. Returns the y the body should start at.
 *
 * Node-safe port of utils/formPdfHelpers.js#addLogoAndFormHeader - same
 * geometry, but the logo comes from the baked asset instead of an <img>.
 */
export function addLogoAndFormHeader(doc, title) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const logoWidth = 70;
  const logoX = 10;
  const titleStartX = logoX + logoWidth + 8;
  const titleMaxWidth = pageWidth - titleStartX - 10;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  const titleLines = doc.splitTextToSize(String(title || ""), titleMaxWidth);
  const lineHeight = 5.2;
  const subtitleGap = 4.5;
  const headerPadding = 8;
  const textBlockHeight = titleLines.length * lineHeight + subtitleGap + 4;
  const headerHeight = Math.max(30, headerPadding * 2 + textBlockHeight);

  drawRoundedRect(doc, 0, 0, pageWidth, headerHeight, 0, PRIMARY_FORM, PRIMARY_FORM);
  drawHeaderLogo(doc, { x: logoX, width: logoWidth, headerHeight });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  const titleStartY = headerPadding + 4;
  titleLines.forEach((line, i) => {
    doc.text(line, titleStartX, titleStartY + i * lineHeight);
  });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(
    "TAYLOR TECHNOLOGY SYSTEMS LIMITED",
    titleStartX,
    titleStartY + titleLines.length * lineHeight + subtitleGap
  );
  return headerHeight + 10;
}

export function applyFooterAllPages(doc, options = {}) {
  const { marginRight = 30, marginBottom = 8, fontSize = 8, color = [150, 150, 150] } = options;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();
  for (let pg = 1; pg <= total; pg += 1) {
    doc.setPage(pg);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);
    doc.text(`Page ${pg} of ${total}`, pageWidth - marginRight, pageHeight - marginBottom);
  }
}

export function getInnerTableWidth(doc, margin = 10) {
  return doc.internal.pageSize.getWidth() - margin * 2;
}

// Accepts whatever shape a signature/photo arrives in (bare base64, a data
// URI, a URL, or a one-element array) and returns something addImage can use.
export function normalizeImageSource(value, fallbackMime = "image/png") {
  if (!value) return "";
  let src = value;
  if (Array.isArray(src)) src = src[0];
  if (src == null) return "";
  if (typeof src !== "string") src = String(src);
  src = src.trim();
  if (!src) return "";
  if (src.startsWith("data:image")) return src;
  if (/^(https?:)?\/\//i.test(src)) return src;
  const compact = src.replace(/\s+/g, "").replace(/,+$/, "");
  if (!compact) return "";
  return `data:${fallbackMime};base64,${compact}`;
}

export function getPdfImageType(imageSrc, fallbackType = "PNG") {
  if (!imageSrc || typeof imageSrc !== "string") return fallbackType;
  const m = imageSrc.match(/^data:image\/([a-zA-Z0-9+.-]+);base64,/i);
  const subtype = m?.[1]?.toLowerCase();
  if (subtype === "png") return "PNG";
  if (subtype === "jpg" || subtype === "jpeg") return "JPEG";
  if (subtype === "webp") return "WEBP";
  return fallbackType;
}

// Draws an image centred inside an autoTable cell, preserving aspect ratio.
// Used for the signature boxes.
export function drawImageInCell(doc, cellHook, value, fallbackMime = "image/png") {
  const src = normalizeImageSource(value, fallbackMime);
  if (!src) return false;
  try {
    const props = doc.getImageProperties(src);
    const cell = cellHook.cell;
    const ratio = Math.min((cell.width - 4) / props.width, (cell.height - 3) / props.height);
    doc.addImage(
      src,
      getPdfImageType(src),
      cell.x + (cell.width - props.width * ratio) / 2,
      cell.y + (cell.height - props.height * ratio) / 2,
      props.width * ratio,
      props.height * ratio,
      undefined,
      "NONE"
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Draws a captured signature into a box, keeping its aspect ratio.
 *
 * The pages did this by loading the base64 into an <img>, painting it onto a
 * 4x canvas over a white background and adding that. jsPDF can read the
 * dimensions straight out of the image data, so none of that is needed - and
 * none of it exists in Node.
 */
export function drawSignature(doc, value, x, y, maxWidth, maxHeight) {
  const src = normalizeImageSource(value);
  if (!src) return false;
  try {
    const props = doc.getImageProperties(src);
    const ratio = Math.min(maxWidth / props.width, maxHeight / props.height);
    const width = props.width * ratio;
    const height = props.height * ratio;
    doc.addImage(src, getPdfImageType(src), x, y, width, height, undefined, "NONE");
    return true;
  } catch {
    // A corrupt or unreadable signature must never sink the whole document.
    return false;
  }
}

export function sanitizeFileNamePart(value) {
  return String(value ?? "")
    .replace(/[\\/:"*?<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

export function toDdMmYyyy(value) {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;

  const dmy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) return `${String(dmy[1]).padStart(2, "0")}-${String(dmy[2]).padStart(2, "0")}-${dmy[3]}`;

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const d = String(parsed.getDate()).padStart(2, "0");
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    return `${d}-${m}-${parsed.getFullYear()}`;
  }
  return raw;
}

export function addedByName(record) {
  const first = record?.addedByFirstName || "";
  const last = record?.addedByLastName || "";
  const joined = `${first} ${last}`.trim();
  return joined || record?.addedBy || "";
}

// "PICOS NAME - SITE - DATE ISSUED.pdf"
export function buildSharePointFileName(parts) {
  const name = parts.map(sanitizeFileNamePart).filter(Boolean).join(" - ");
  return `${name || "Form"}.pdf`;
}
